import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { get } from '../../api/coreApi';
import './EzeeEconomicsPanel.css';
import {EzeeUsageForecastPanel} from './EzeeUsageForecastPanel';
import {DigitalOceanBillingPanel} from './DigitalOceanBillingPanel';

type Aggregate = {
  calls: number; inputTokens: number; outputTokens: number;
  estimatedCostUsd: number; averageLatencyMs: number | null;
  cachedInputTokens: number; cacheReportedCalls: number;
};
type Dimension = Aggregate & {
  taskType?: string; provider?: string; modelId?: string; model?: string;
  day?: string; status?: string; costBasis?: string;
  failedCalls?: number; recordedSuccessRate?: number | null; measuredCacheTokens?: number | null;
};
export type EzeeEconomics = {
  scope: 'routed_inference_only';
  currency: 'USD';
  costAccuracy: string;
  period: { key: string; start: string; endExclusive: string; current: boolean; daysInMonth: number };
  totals: Aggregate & {
    estimatedSpentUsd: number; reservedUsd: number; estimatedCommittedUsd: number;
    averageCostPerCallUsd: number | null; failedCalls: number; estimatedCostDifferenceUsd: number;
  };
  budget: {
    configuredUsd: number | null; percentCommitted: number | null;
    remainingUsd: number | null; projectedMonthUsd: number | null;
  };
  byTask: Dimension[]; byModel: Dimension[]; byDay: Dimension[]; byBasis: Dimension[];
  evaluations: Array<{runId:number;scope:string;score:number|null;totalCases:number;passedCases:number;failedCases:number;finishedAt:string}>;
  qualityNote: string;
  vendorReconciliation: {state:string;note:string;rows:Array<{provider:string;statementRef:string;
    invoicedUsd:number;routedEstimatedUsd:number;deltaUsd:number;comparable:boolean;reason:string}>};
  coverage: { includes: string; excludes: string; note: string };
};

const usd = (value: number | null | undefined, precision = 2) =>
  value == null || !Number.isFinite(Number(value))
    ? '—' : '$' + Number(value).toLocaleString('en-US', {
      minimumFractionDigits: precision, maximumFractionDigits: precision,
    });

const integer = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString('en-US');

const recentMonths = () => {
  const now = new Date();
  return Array.from({length:12},(_,offset) => {
    const dt = new Date(Date.UTC(now.getUTCFullYear(),now.getUTCMonth()-offset,1));
    return {key:dt.toISOString().slice(0,7),label:dt.toLocaleString('en-US',{
      month:'long',year:'numeric',timeZone:'UTC',
    })};
  });
};

function labelTask(task?: string) {
  const names: Record<string,string> = {
    brain_chat:'Full Brain conversation',
    brain_smalltalk:'Small talk',
    brain_general_knowledge:'General knowledge',
    brain_text_edit:'Lightweight writing',
    email_reply:'Email replies',
    email_briefing:'Email briefings',
    operational_analysis:'Operational analysis',
    executive_report:'Executive reports',
    improvement_recommendation:'Improvement research',
    improvement_preparation:'Engineering preparation',
    improvement_simulation:'Improvement simulation',
    improvement_evaluation:'Quality evaluations',
  };
  return names[task || ''] || task || 'Unknown task';
}

export const EconomicsTable: React.FC<{
  title:string; rows:Dimension[]; kind:'model'|'task';
}> = ({title, rows, kind}) => (
  <div className="ezee-economics__panel">
    <h4>{title}</h4>
    {rows.length ? <div className="ezee-economics__table-scroll">
      <table className="ezee-economics__table">
        <thead><tr><th scope="col">{kind==='model'?'Model':'Workload'}</th>
          <th scope="col">Calls</th><th scope="col">Input / Output</th>
          <th scope="col">Avg. response</th>
          {kind==='model'?<th scope="col">Success / Cached</th>:null}
          <th scope="col">Est. USD</th>
        </tr></thead>
        <tbody>{rows.map(row=><tr key={kind==='model'?row.modelId:row.taskType}>
          <td><strong>{kind==='model'?(row.modelId||'Unknown'):labelTask(row.taskType)}</strong>
            {kind==='model'&&row.provider?<small>{row.provider}</small>:null}</td>
          <td>{integer(row.calls)}</td>
          <td>{integer(row.inputTokens)} / {integer(row.outputTokens)}</td>
          <td>{row.averageLatencyMs==null?'—':(row.averageLatencyMs/1000).toFixed(2)+'s'}</td>
          {kind==='model'?<td>{row.recordedSuccessRate==null?'Not measured':row.recordedSuccessRate.toFixed(1)+'%'}
            <small>{row.measuredCacheTokens==null?'Cache usage unknown':integer(row.measuredCacheTokens)+' cached tokens'}</small></td>:null}
          <td>{usd(row.estimatedCostUsd,row.estimatedCostUsd>0&&row.estimatedCostUsd<0.01?4:2)}</td>
        </tr>)}</tbody>
      </table>
    </div>:<p className="ezee-economics__muted">No routed model calls recorded this month.</p>}
  </div>
);

export const EzeeEconomicsPanel: React.FC<{ instanceUniqueId:string|null }> = ({instanceUniqueId}) => {
  const [period, setPeriod] = useState<string>(()=>recentMonths()[0].key);
  const [data, setData] = useState<EzeeEconomics|null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const months = useMemo(recentMonths, []);
  const fetchData = useCallback(() => setReload(x=>x+1), []);
  useEffect(()=>{
    if (!instanceUniqueId) { setData(null);setError('Load an Elite Core instance to view Ezee economics.');return; }
    let cancelled = false;
    setData(null);setLoading(true);setError('');
    get<{success:boolean; economics:EzeeEconomics}>(
      `/ai/instances/${encodeURIComponent(instanceUniqueId)}/model-routing/economics`,
      {params:{month:period}},
    ).then(response=>{
      if (!cancelled) setData(response.economics);
    }).catch((cause:unknown)=>{
      if (!cancelled) setError(cause instanceof Error?cause.message:'Unable to load Ezee economics.');
    }).finally(()=>{if(!cancelled)setLoading(false)});
    return ()=>{cancelled=true};
  },[instanceUniqueId,period,reload]);
  const t = data?.totals;
  const budget = data?.budget;
  const highestDay = Math.max(0,...(data?.byDay||[]).map(row=>Number(row.estimatedCostUsd||0)));
  const committedPercent = budget?.percentCommitted ?? null;
  return <section className="ezee-economics" aria-label="Ezee routed inference economics">
    <div className="ezee-economics__head">
      <div><h3>Ezee inference economics</h3>
        <p>Recorded model spend, efficiency and budget pacing for this Elite Core instance.</p></div>
      <div className="ezee-economics__actions">
        <label htmlFor="ezee-economics-month">Reporting month</label>
        <select id="ezee-economics-month" value={period} onChange={e=>setPeriod(e.target.value)}>
          {months.map(m=><option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
        <button type="button" onClick={fetchData} disabled={!instanceUniqueId||loading}>Refresh</button>
      </div>
    </div>
    <p className="ezee-economics__scope"><strong>Scope: routed inference only.</strong> This is estimated USD spending based on developer-maintained model prices, not total Ezee expenditure or provider invoicing.</p>
    {error?<p role="alert" className="ezee-economics__error">{error}</p>:null}
    {loading?<p role="status">Loading Ezee economics…</p>:null}
    {data && t ? <>
      <div className="ezee-economics__metrics">
        {[
          {label:'Estimated charged',value:usd(t.estimatedSpentUsd,4),detail:'Completed + conservatively charged failed attempts'},
          {label:'Reserved in flight',value:usd(t.reservedUsd,4),detail:'Pending provider attempts'},
          {label:'Total model calls',value:integer(t.calls),detail:integer(t.failedCalls)+' recorded failures'},
          {label:'Cost per model call',value:usd(t.averageCostPerCallUsd,5),detail:integer(t.inputTokens+t.outputTokens)+' tokens recorded'},
        ].map(m=><div key={m.label} className="ezee-economics__metric">
          <span>{m.label}</span><strong>{m.value}</strong><small>{m.detail}</small>
        </div>)}
      </div>
      <div className="ezee-economics__panel">
        <h4>Budget & pace {data.period.current?'(current month)':'(historical month)'}</h4>
        {budget?.configuredUsd != null ? <>
          <div className="ezee-economics__budget-text">
            <strong>{usd(t.estimatedCommittedUsd,4)} committed / {usd(budget.configuredUsd)} configured</strong>
            <span>{committedPercent==null?'No active spending cap':committedPercent.toFixed(1)+'% used'}</span>
          </div>
          <div className="ezee-economics__progress" role="progressbar"
            aria-label="Estimated monthly model budget committed"
            aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(100,committedPercent||0)}>
            <span style={{width:Math.min(100,committedPercent||0)+'%'}}/>
          </div>
          <p>Estimated remaining: <strong>{usd(budget.remainingUsd,4)}</strong>
            {' '}· Month-end run-rate projection: <strong>{usd(budget.projectedMonthUsd)}</strong></p>
          {committedPercent!==null && committedPercent>=80?
            <p role="alert" className="ezee-economics__warning">Estimated routed-model commitments reached {committedPercent.toFixed(1)}% of the configured budget.</p>:null}
        </> : <p className="ezee-economics__muted">Historical report: current instance budget settings are not applied retroactively.</p>}
      </div>
      <div className="ezee-economics__panel">
        <h4>Daily estimated charges</h4>
        {data.byDay.length? <div className="ezee-economics__days" role="list" aria-label="Estimated daily inference spend">
          {data.byDay.map(row=><div className="ezee-economics__day" role="listitem" key={row.day}>
            <span>{String(row.day).slice(5)}</span>
            <div title={usd(row.estimatedCostUsd,4)}><span style={{width:(highestDay>0?row.estimatedCostUsd/highestDay*100:0)+'%'}}/></div>
            <strong>{usd(row.estimatedCostUsd,4)}</strong>
          </div>)}
        </div>:<p className="ezee-economics__muted">No routed charges for this period.</p>}
      </div>
      <div className="ezee-economics__panels">
        <EconomicsTable title="Spend by AI model" rows={data.byModel} kind="model"/>
        <EconomicsTable title="Spend by Ezee workload" rows={data.byTask} kind="task"/>
      </div>
      <div className="ezee-economics__panel">
        <h4>Evaluation history — advisory quality evidence</h4>
        <p className="ezee-economics__muted">{data.qualityNote}</p>
        {data.evaluations?.length ? <div className="ezee-economics__table-scroll">
          <table className="ezee-economics__table">
            <thead><tr><th>Evaluation</th><th>Cases</th><th>Passed</th><th>Suite score</th><th>Finished</th></tr></thead>
            <tbody>{data.evaluations.map(row=><tr key={row.runId}>
              <td>{row.scope} #{row.runId}</td>
              <td>{integer(row.totalCases)}</td>
              <td>{integer(row.passedCases)}</td>
              <td>{row.score==null?'Unknown':(Number(row.score)*100).toFixed(1)+'%'}</td>
              <td>{row.finishedAt?new Date(row.finishedAt).toLocaleDateString():'—'}</td>
            </tr>)}</tbody>
          </table>
        </div> : <p className="ezee-economics__muted">No completed Ezee evaluation suites recorded for this month.</p>}
      </div>
      <div className="ezee-economics__panel">
        <h4>Provider billing reconciliation</h4>
        <p className="ezee-economics__muted">Only developer-provided statements confirmed to use dedicated instance/provider credentials can appear here. A billing difference is not proof of overcharging or savings.</p>
        {data.vendorReconciliation?.rows.length ?
          <div className="ezee-economics__table-scroll">
            <table className="ezee-economics__table">
              <thead><tr><th>Provider / Statement</th><th>Vendor billed USD</th>
                <th>Recorded routed estimate</th><th>Difference</th></tr></thead>
              <tbody>{data.vendorReconciliation.rows.map(row=><tr key={row.provider+':'+row.statementRef}>
                <td><strong>{row.provider}</strong><small>{row.statementRef}</small></td>
                <td>{usd(row.invoicedUsd,4)}</td>
                <td>{usd(row.routedEstimatedUsd,4)}</td>
                <td>{usd(row.deltaUsd,4)}<small>Scope requires review</small></td>
              </tr>)}</tbody>
            </table>
          </div>
          : <p className="ezee-economics__muted">No dedicated, verified vendor statement is configured for this month ({data.vendorReconciliation?.state || 'unavailable'}).</p>}
      </div>
      <div className="ezee-economics__panel">
        <h4>Charge confidence & failures</h4>
        <p className="ezee-economics__muted">Reported provider tokens and reserved estimates have different confidence. Failed requests may still incur charges.</p>
        <div className="ezee-economics__table-scroll"><table className="ezee-economics__table">
          <thead><tr><th>Cost basis</th><th>Result</th><th>Calls</th><th>Est. USD</th></tr></thead>
          <tbody>{data.byBasis.length?data.byBasis.map(row=><tr key={row.costBasis+':'+row.status}>
            <td>{(row.costBasis||'unknown').replace(/_/g,' ')}</td>
            <td>{row.status}</td><td>{integer(row.calls)}</td><td>{usd(row.estimatedCostUsd,4)}</td>
          </tr>):<tr><td colSpan={4}>No recorded attempts.</td></tr>}</tbody>
        </table></div>
      </div>
      <p className="ezee-economics__scope"><strong>Coverage limitations:</strong> {data.coverage.excludes} {data.coverage.note}</p>
    </>:null}
    <EzeeUsageForecastPanel instanceUniqueId={instanceUniqueId}/>
    <DigitalOceanBillingPanel period={period}/>
  </section>;
};
